import { dropTask } from 'ember-concurrency';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import * as v from 'valibot';
import { strategyFinderFormSchema } from '../../validation-schema/forms/loan-forgiveness-estimator.ts';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { ErrorSignature } from '../../../types/global.ts';
import errorsForField from '../../helpers/errors-for-field.ts';
import { CheckboxGroup, RadioGroup } from 'tio-ui/components/forms';
import Input from 'tio-reimagined/components/input';
import Select from 'tio-reimagined/components/select';
import inputmask from '../../modifiers/inputmask.ts';
import type Store from '@ember-data/store';
import t from 'ember-intl/helpers/t';
import TioAlert from '../tio/alert.gts';
import Popover from 'tio-reimagined/components/popover';
import type { IntlService } from 'ember-intl';
import type { LoanSummary } from '../../types/loan-summary.ts';
import type RecommendationModel from '../../models/recommendation.ts';
import type { SummaryByLoanType } from '../../types/loan-summary.ts';
import type UserModel from '../../models/user.ts';
import { stateOptions, grossIncomes, loanForgivenessOptions } from './consts.ts';
export { stateOptions };
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';

type FormModel = {
  payoffGoal: string;
  affordPayments: string;
  federalLoanTime: string;
  currentlyWorkingToward: string;
  currentForgivenessProgram?: string;
  madePayments: number;
  employmentType: string;
  madeLimitedWaiverPayments: number;
  planInGov?: string;
  planAsTeacher?: string;
  householdIncome: number;
  householdSize: number;
  residenceState: string;
  academicDegree: string;
};

interface StrategyFinderFormSignature {
  Args: {
    user: UserModel;
    loanSummary: LoanSummary;
    application: string;
    isAgent?: boolean;
    didSave: (recommendation: RecommendationModel) => void;
  };
  Element: HTMLElement;
}

export default class StrategyFinderFormComponent extends Component<StrategyFinderFormSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  @tracked affordPayments: string = '';
  @tracked currentForgivenessProgram = '';
  @tracked currentlyWorkingToward: string = '';
  @tracked degree: string = '';
  @tracked employmentType: string = '';
  @tracked federalLoanTime: string = '';
  @tracked hasSubmitted: boolean | false = false;
  @tracked householdSize: number | 1 = 1;
  @tracked income: string | number = 0;
  @tracked incomeToSend: number | 0 = 0;
  @tracked isGov: boolean = false;
  @tracked isOther: boolean = false;
  @tracked isTeacher: boolean = false;
  @tracked madeLimitedWaiverPayments: number | 0 = 0;
  @tracked numberOfPayments: number | 1 = 1;
  @tracked payoffGoal: string = '';
  @tracked planAsTeacher: string = '';
  @tracked planInGov: string = '';
  @tracked programToSend: string = '';
  @tracked selectedState = '';
  @tracked showCustomIncome: boolean | false = false;
  @tracked showIncompleteAlert = false;
  @tracked stateOfResidence: string = '';
  @tracked submitError: ErrorSignature | null = null;

  get states() {
    return stateOptions.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  get employmentSectorOptions() {
    return [
      {
        dataKey: 'isGov',
        value: this.isGov,
        text: 'common.strategy_finder.gov_pub_tribal_nonp',
      },
      {
        dataKey: 'isTeacher',
        value: this.isTeacher,
        text: 'common.strategy_finder.teacher_low_income',
      },
      {
        dataKey: 'isOther',
        value: this.isOther,
        text: 'common.strategy_finder.other_or_private',
      },
    ];
  }

  setEmploymentType = () => {
    if (this.isGov && this.isTeacher) {
      this.employmentType = 'teacher&government-tribal-nonprofit';
    } else if (this.isOther && !this.isTeacher && !this.isGov) {
      this.employmentType = 'other-private-sector';
    } else if (this.isTeacher) {
      this.employmentType = 'teacher';
    } else if (this.isGov) {
      this.employmentType = 'government-tribal-nonprofit';
    } else {
      this.employmentType = '';
    }
  };

  get loanSummariesByType() {
    return this.args.loanSummary.summaryByLoanType || [];
  }

  get formModel() {
    this.setEmploymentType();
    return {
      payoffGoal: this.payoffGoal,
      affordPayments: this.affordPayments,
      federalLoanTime: this.federalLoanTime,
      currentlyWorkingToward: this.currentlyWorkingToward,
      currentForgivenessProgram:
        this.currentlyWorkingToward === 'yes' ? this.programToSend : undefined,
      madePayments: this.numberOfPayments,
      employmentType: this.employmentType,
      madeLimitedWaiverPayments: this.madeLimitedWaiverPayments,
      planInGov: this.isGov ? this.planInGov : undefined,
      planAsTeacher: this.isTeacher ? this.planAsTeacher : undefined,
      householdIncome: this.incomeToSend,
      householdSize: this.householdSize,
      residenceState: this.stateOfResidence,
      academicDegree: this.degree,
    };
  }

  get formValidationErrors() {
    const schema = strategyFinderFormSchema;
    try {
      v.parse(schema, this.formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  setStateOfResidence = (value: string) => {
    this.stateOfResidence = value;
    this.selectedState = value;
  };

  setIncome = (value: string) => {
    if (value === 'Custom') {
      this.showCustomIncome = true;
      this.incomeToSend = 0;
      this.income = 'Custom';
    } else {
      this.showCustomIncome = false;
      this.incomeToSend = parseInt(value, 10);
      this.income = parseInt(value, 10);
    }
  };

  setCustom = (_value: string, event?: InputEvent) => {
    // @ts-expect-error inputmask is not a valid property on Event
    const unmaskedValue = event?.target?.inputmask?.unmaskedvalue();

    if (typeof unmaskedValue === 'string' && unmaskedValue.trim() !== '') {
      const dollars = Number(unmaskedValue);

      if (!Number.isNaN(dollars)) {
        this.incomeToSend = Math.round(dollars * 100);
      }
    } else if (typeof unmaskedValue === 'number' && !Number.isNaN(unmaskedValue)) {
      this.incomeToSend = Math.round(unmaskedValue * 100);
    }
  };

  setProgram = (value: string) => {
    this.currentForgivenessProgram = value;
    this.programToSend = value;
  };

  setMadeLimitedWaiverPayments = (value: string) => {
    this.madeLimitedWaiverPayments = parseInt(value, 10);
  };

  setNumberOfPayments = (value: string) => {
    this.numberOfPayments = parseInt(value, 10);
  };

  setHouseholdSize = (value: string) => {
    this.householdSize = parseInt(value, 10);
  };

  submitTask = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;

    if (this.formValidationErrors.length) {
      this.showIncompleteAlert = true;
      return;
    }
    try {
      const saved = await this.saveFormValues.perform(this.formModel);
      this.hasSubmitted = false;
      this.args.didSave?.(saved);
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  saveFormValues = dropTask(async (formModel: FormModel) => {
    const app = this.args.application;
    const calculationInput = this.formatPayload(formModel);
    const recommendation = await this.store.createRecord<RecommendationModel>('recommendation', {
      user: this.args.user,
      calculationInput: { data: calculationInput },
      requestingApp: app,
    });
    this.storeAnswersOnProfile(formModel);
    return await recommendation.save();
  });

  storeAnswers = async (providingApp = 'DASHBOARD', answers = {}) => {
    let profile = this.args.user.answerProfile;
    if (!profile) {
      profile = await this.store.createRecord('answer-profile', {
        providingApp: 'DASHBOARD',
        user: this.args.user,
      });
    }
    profile.providingApp = providingApp;
    for (const key in answers) {
      // @ts-expect-error: Find out possible values.
      profile[key] = answers[key];
    }
    return await profile.save();
  };

  storeAnswersOnProfile(formModel: FormModel) {
    // We don't need this method to be awaited in the context of the estimator flow.
    // The answerProfile service will persist and finish this request, so as not
    // to interfere with the wait time or overall user experience of the estimator.
    try {
      const app = this.args.application;
      this.storeAnswers(app, formModel);
    } catch (e) {
      // Catch and log but don't re-throw so as not to block estimator experience.
      console.error(e);
    }
  }

  formatPayload(formModel: FormModel) {
    const {
      payoffGoal,
      affordPayments,
      federalLoanTime,
      currentlyWorkingToward,
      currentForgivenessProgram,
      madePayments,
      employmentType,
      madeLimitedWaiverPayments,
      planInGov,
      planAsTeacher,
      householdIncome,
      householdSize,
      residenceState,
      academicDegree,
    } = formModel;

    return this.loanSummariesByType.map((loanData: SummaryByLoanType) => {
      const {
        loanType,
        totalDisbursedAmount,
        repaymentPlan,
        // federalLoanTime,
        currentBalance,
        interestRate,
        monthlyPayment,
      } = loanData;

      const formatted = {
        'data-style': 'sf',
        purpose: {
          'afford-payments': affordPayments,
          'payoff-goal': payoffGoal,
        },
        'loan-type': {
          'federal-loan-type': {
            'loan-type': loanType,
            'original-disbursement-amount': totalDisbursedAmount,
            'monthly-payment': monthlyPayment,
            'repayment-plan': repaymentPlan,
          },
          // should this be removed from form in favor of loan summary federalLoanTime data? - Julia 01/23/2023
          'federal-loan-time': federalLoanTime,
        },
        forgiveness: {
          'current-working': currentlyWorkingToward,
          'made-payments': madePayments,
          'current-forgiveness-program': currentForgivenessProgram,
          'academic-degree': academicDegree,
        },
        employment: {
          'employment-type': employmentType,
          'plan-in-government': planInGov,
          'plan-as-teacher': planAsTeacher,
          'made-limited-waiver-payments': madeLimitedWaiverPayments,
        },
        'loan-details': {
          'household-income': householdIncome,
          'current-balance': currentBalance,
          'interest-rate': interestRate,
          'household-size': householdSize,
          'residence-state': residenceState,
        },
      };
      return formatted;
    });
  }

  toggle = (key: string) => {
    // @ts-expect-error: make a helper eventually
    this[key] = !this[key];
  };

  <template>
    <form ...attributes>
      {{! Payoff Goal }}
      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "payoffGoal" schemaErrors=this.formValidationErrors}}
        @label={{t "common.strategy_finder.your_payoff_goal"}}
        @onChange={{fn (mut this.payoffGoal)}}
        @value={{this.payoffGoal}}
        as |Radio|
      >
        <p class="text-xs mb-3">{{t "common.strategy_finder.payoff_goal_blurb"}}</p>
        <Radio @value="quick" @label={{t "common.strategy_finder.payoff_quickly"}} />
        <Radio @value="slow" @label={{t "common.strategy_finder.lower_monthly"}} />
      </RadioGroup>

      {{! Afford Payments }}
      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "affordPayments" schemaErrors=this.formValidationErrors}}
        @label={{t "common.forgiveness_estimator.afford_payments"}}
        @onChange={{fn (mut this.affordPayments)}}
        @value={{this.affordPayments}}
        as |Radio|
      >
        <p class="text-xs mt-1 mb-3">{{t "common.strategy_finder.lower_monthly_blurb"}}</p>
        <Radio @value="yes" @label={{t "common.yes"}} />
        <Radio @value="no" @label={{t "common.no"}} />
      </RadioGroup>

      {{! Federal Loan Time }}
      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "federalLoanTime" schemaErrors=this.formValidationErrors}}
        @label={{t "common.forgiveness_estimator.before_2007"}}
        @onChange={{fn (mut this.federalLoanTime)}}
        @value={{this.federalLoanTime}}
        as |Radio|
      >
        <Radio @value="before-october-2007" @label="Yes" />
        <Radio @value="after-october-2007" @label="No" />
      </RadioGroup>

      {{! Currently Working Toward }}
      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "currentlyWorkingToward" schemaErrors=this.formValidationErrors}}
        @label={{t "common.strategy_finder.currently_working_toward"}}
        @onChange={{fn (mut this.currentlyWorkingToward)}}
        @value={{this.currentlyWorkingToward}}
        as |Radio|
      >
        <Radio @value="yes" @label={{t "common.yes"}} />
        {{#if (eq this.currentlyWorkingToward "yes")}}
          <div class="py-2 ml-7">
            {{! Which Forgiveness Program }}
            <div class="pb-2">
              <label for="currentForgivenessProgram" class="form-field-label">
                {{t "common.strategy_finder.which_program"}}
              </label>
              <Select
                @errors={{errorsForField
                  "currentForgivenessProgram"
                  schemaErrors=this.formValidationErrors
                }}
                @onChange={{this.setProgram}}
                @items={{loanForgivenessOptions}}
                @selected={{this.currentForgivenessProgram}}
                id="currentForgivenessProgram"
              />
            </div>

            {{! Estimated Payments }}
            <Input
              @errors={{errorsForField "madePayments" schemaErrors=this.formValidationErrors}}
              @label={{t "common.strategy_finder.how_many_payments"}}
              @onChange={{this.setNumberOfPayments}}
              type="number"
              @value={{this.numberOfPayments}}
              autocomplete="off"
              min="1"
              required={{eq this.currentlyWorkingToward "yes"}}
            />
          </div>
        {{/if}}
        <Radio
          @value="no-and-not-interested"
          @label={{t "common.strategy_finder.not_interested"}}
        />
        <Radio @value="no-but-want-learn" @label={{t "common.strategy_finder.not_yet"}} />
      </RadioGroup>

      {{! Employment Sector }}
      <CheckboxGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "employmentType" schemaErrors=this.formValidationErrors}}
        @label={{t "common.strategy_finder.check_applicable_employment_types"}}
        as |Checkbox|
      >
        <p class="text-xs mt-1 mb-3">{{t "common.strategy_finder.forgiveness_eligibility"}}</p>
        {{#each this.employmentSectorOptions as |employmentSector index|}}
          <Checkbox
            @checked={{employmentSector.value}}
            class="form-field-label"
            @label={{t employmentSector.text}}
            @onChange={{fn this.toggle employmentSector.dataKey}}
            data-index={{index}}
            id={{employmentSector.dataKey}}
          />

          {{#if employmentSector.value}}
            {{#if (eq employmentSector.dataKey "isGov")}}
              <div class="ml-7 my-2">

                {{! PSLF Plan }}
                <RadioGroup
                  data-legacy-input
                  class="py-2"
                  @errors={{errorsForField "planInGov" schemaErrors=this.formValidationErrors}}
                  @label="{{t 'common.strategy_finder.gov_how_long'}}"
                  @onChange={{fn (mut this.planInGov)}}
                  @value={{this.planInGov}}
                  as |Radio|
                >
                  <p class="text-xs mt-1 mb-3">{{t "common.strategy_finder.pslf_requires"}}</p>
                  <Radio
                    @value="for-10-years-more"
                    @label={{t "common.strategy_finder.at_least_ten"}}
                  />
                  <Radio
                    @value="not-for-10-years"
                    @label={{t "common.strategy_finder.ten_years_unlikely"}}
                  />
                </RadioGroup>

                {{! Government Payments Since 2007 }}
                <div class="py-4">
                  <label for="madeLimitedWaiverPaymentsGov" class="form-field-label">
                    {{t "common.strategy_finder.gov_how_many_months"}}
                  </label>
                  <Input
                    @errors={{errorsForField
                      "madeLimitedWaiverPayments"
                      schemaErrors=this.formValidationErrors
                    }}
                    @onChange={{this.setMadeLimitedWaiverPayments}}
                    type="number"
                    @value={{this.madeLimitedWaiverPayments}}
                    autocomplete="off"
                    id="madeLimitedWaiverPaymentsGov"
                    required={{this.isGov}}
                  />
                </div>
              </div>
            {{/if}}
            {{#if (eq employmentSector.dataKey "isTeacher")}}
              <div class="ml-7 my-2">
                <TioAlert @type="info" @allowDismiss={{false}}>
                  <:body>
                    <p class="text-sm">{{t "common.strategy_finder.if_public_school"}}</p>
                  </:body>
                </TioAlert>

                {{! Teacher Plan }}
                <RadioGroup
                  data-legacy-input
                  class="py-4"
                  @errors={{errorsForField "planAsTeacher" schemaErrors=this.formValidationErrors}}
                  @label={{t "common.strategy_finder.teacher_how_long"}}
                  @onChange={{fn (mut this.planAsTeacher)}}
                  @value={{this.planAsTeacher}}
                  as |Radio|
                >
                  <p class="text-xs mt-1 mb-3">
                    {{t "common.strategy_finder.teacher_forgiveness_rec"}}
                  </p>
                  <Radio
                    @value="for-5-years-and-more"
                    @label={{t "common.strategy_finder.teacher_five_years"}}
                  />
                  <Radio
                    @value="less-than-5-years"
                    @label={{t "common.strategy_finder.teacher_five_years_unlikely"}}
                  />
                </RadioGroup>

                {{! Teacher Payments Since 1994 }}
                <div class="py-4">
                  <label for="madeLimitedWaiverPayments" class="form-field-label">
                    {{t "common.strategy_finder.payments_since_94"}}
                  </label>
                  <Input
                    @errors={{errorsForField
                      "madeLimitedWaiverPayments"
                      schemaErrors=this.formValidationErrors
                    }}
                    @onChange={{this.setMadeLimitedWaiverPayments}}
                    type="number"
                    @value={{this.madeLimitedWaiverPayments}}
                    autocomplete="off"
                    id="madeLimitedWaiverPayments"
                    required={{this.isTeacher}}
                  />
                </div>
              </div>
            {{/if}}
          {{/if}}
        {{/each}}
      </CheckboxGroup>

      {{! Personal Details }}
      <div class="pt-2 pb-4">
        <p class="font-semibold form-field-label">
          {{t "common.strategy_finder.details_to_calculate_payments"}}
        </p>
        <p class="text-xs mt-1 mb-3">{{t "common.strategy_finder.details_blurb"}}</p>
        <div class="pb-1">
          {{! Household Income }}
          <div>
            <Popover @triggerMode="hover">
              <:trigger as |pop|>
                <span {{pop.register}} {{pop.hoverHandlers}}>
                  <label for="householdIncome" class="form-field-label !text-sm">
                    {{t "common.forgiveness_estimator.annual_income"}}</label>
                </span>
              </:trigger>
              <:body>
                {{t "common.forgiveness_estimator.annual_income_tooltip" placement="top"}}
              </:body>
            </Popover>
            <Select
              @errors={{errorsForField "householdIncome" schemaErrors=this.formValidationErrors}}
              @onChange={{this.setIncome}}
              @items={{grossIncomes}}
              @selected={{String this.income}}
              id="householdIncome"
            />
          </div>

          {{! Custom Income Amount }}
          {{#if this.showCustomIncome}}
            <div class="pb-3">
              <Input
                @errors={{errorsForField "householdIncome" schemaErrors=this.formValidationErrors}}
                @onChange={{this.setCustom}}
                required={{eq this.showCustomIncome true}}
                autocomplete="off"
                id="custom-inc"
                {{inputmask
                  alias="currency"
                  prefix="$"
                  unmaskAsNumber=true
                  digits="0"
                  digitsOptional=false
                }}
              />
            </div>
          {{/if}}

          {{! Household Size }}
          <div class="pb-1">
            <Popover @triggerMode="hover">
              <:trigger as |pop|>
                <span {{pop.register}} {{pop.hoverHandlers}}>
                  <label for="householdSize" class="form-field-label !text-sm">
                    {{t "common.strategy_finder.family_size"}}
                  </label>
                </span>
              </:trigger>
              <:body>
                {{t "common.strategy_finder.family_size_tooltip"}}
              </:body>
            </Popover>
            <Input
              @errors={{errorsForField "householdSize" schemaErrors=this.formValidationErrors}}
              @onChange={{this.setHouseholdSize}}
              required="true"
              type="number"
              @value={{this.householdSize}}
              autocomplete="off"
              min="1"
              id="householdSize"
            />
          </div>
          {{! State Picker }}
          <div>
            <label for="residenceState" class="form-field-label !text-sm">
              {{t "common.strategy_finder.state_of_residence"}}
            </label>
            <Select
              @searchable={{true}}
              @errors={{errorsForField "residenceState" schemaErrors=this.formValidationErrors}}
              @onChange={{this.setStateOfResidence}}
              @items={{this.states}}
              @selected={{this.selectedState}}
              id="residenceState"
            />
          </div>
        </div>
      </div>
      {{! Academic degree }}
      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "academicDegree" schemaErrors=this.formValidationErrors}}
        @label={{t "common.forgiveness_estimator.grad_or_undergrad"}}
        @onChange={{fn (mut this.degree)}}
        @value={{this.degree}}
        as |Radio|
      >
        <Radio
          class="accent-black"
          @label={{t "common.forgiveness_estimator.undergradOnly"}}
          @value="undergradOnly"
        />
        <Radio @value="GradOnly" @label={{t "common.forgiveness_estimator.gradOnly"}} />
        <Radio @value="undergradGrad" @label={{t "common.forgiveness_estimator.undergradGrad"}} />
      </RadioGroup>

      {{#if this.showIncompleteAlert}}
        <p class="form-field-feedback form-field-feedback--error">
          {{t "common.strategy_finder.please_complete"}}
        </p>
      {{/if}}
      <Button
        @intent="primary"
        @isRunning={{this.submitTask.isRunning}}
        {{on "click" this.submitTask.perform}}
      >
        {{t "common.forgiveness_estimator.see_results"}}
      </Button>
    </form>
  </template>
}
